<template>
  <tr>
    <td>
      {{ device.label }}
    </td>
    <td class="text-xs-left">
      <v-edit-dialog
        :return-value.sync="device._name"
        lazy
        large
        cancel-text="Annuler"
        save-text="Sauver"
        @open="$set(device, '_name', device.display_name);"
        @save="$set(device, 'display_name', device._name); saveDevice(device);"
      >
        {{ device.display_name }}
        <v-text-field
          slot="input"
          v-model="device._name"
          :rules="[max15chars, min3chars]"
          label="Nom"
          single-line
          counter
        />
      </v-edit-dialog>
    </td>
    <td class="text-xs-left">
      <v-edit-dialog
        lazy
      >
        <span
          class="elac-coloricon"
          :style="{ background: color }"
        />
        <color-picker
          slot="input"
          v-model="color"
          @input="saveDevice(device);"
        />
      </v-edit-dialog>
    </td>
    <td class="text-xs-left">
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-icon
            v-if="heartbeatIcon"
            :color="heartbeatColor"
            v-on="on"
          >
            {{ heartbeatIcon }}
          </v-icon>
        </template>
        <span>{{ heartbeatText }}</span>
      </v-tooltip>
    </td>
    <td class="text-xs-left">
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-icon
            v-if="batteryIcon"
            :color="batteryColor"
            v-on="on"
          >
            {{ batteryIcon }}
          </v-icon>
        </template>
        <span>{{ device.battery }}%</span>
      </v-tooltip>
    </td>
    <td class="text-xs-right">
      <v-icon @click="showDevice(device)">
        {{ `mdi-eye${ device.show ? '' : '-off'}` }}
      </v-icon>
      <v-icon
        class="ml-1"
        @click="trackDevice(device)"
      >
        {{ `mdi-crosshairs${ device.track ? '-gps' : ''}` }}
      </v-icon>
    </td>
  </tr>
</template>

<script>
import Vue from 'vue';
import ms from 'ms';
import { Compact } from 'vue-color';

export default {
  components: {
    'color-picker': Compact,
  },

  props: {
    device: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    currentTime: 0,
    max15chars: v => !!v && (v.length <= 15 || 'Nom trop long!'),
    min3chars: v => !!v && (v.length >= 3 || 'Nom trop court!'),
  }),

  computed: {
    color: {
      get() {
        return this.device.color || 'black';
      },

      set(color) {
        this.device.color = color.hex;
      },
    },

    batteryIcon() {
      const b = this.device.battery;

      if (!b) { return null; }

      if (b <= 15) { return 'mdi-battery-alert'; }
      if (b <= 25) { return 'mdi-battery-20'; }
      if (b <= 40) { return 'mdi-battery-30'; }
      if (b <= 55) { return 'mdi-battery-50'; }
      if (b <= 70) { return 'mdi-battery-60'; }
      if (b <= 85) { return 'mdi-battery-80'; }
      if (b <= 95) { return 'mdi-battery-90'; }

      return 'mdi-battery';
    },

    batteryColor() {
      const b = this.device.battery;

      if (!b) { return null; }
      if (b <= 15) { return 'red'; }
      if (b <= 30) { return 'orange'; }

      return 'green';
    },

    delay() {
      let delay = Infinity;
      const time = this.currentTime;
      const heartbeat = this.device.heartbeat || 0;
      let timestamp = 0;

      if (this.device.location && this.device.location.timestamp) {
        timestamp = new Date(this.device.location.timestamp);
      }

      if (timestamp > heartbeat) {
        delay = Date.now() - timestamp;
      } else if (heartbeat) {
        delay = Date.now() - heartbeat;
      }

      if (this.device.location && time - this.device.location.lastSeen > delay) {
        delay = time - this.device.location.lastSeen;
      }

      return delay;
    },

    heartbeatIcon() {
      if (this.delay === Infinity) {
        return null;
      }

      if (this.delay < Vue.prototype.$config.map.deviceLagDelay) { return 'mdi-wifi-strength-4'; }
      if (this.delay < Vue.prototype.$config.map.deviceTimeoutDelay) { return 'mdi-wifi-strength-4-alert'; }

      return 'mdi-wifi-strength-off-outline';
    },

    heartbeatColor() {
      if (this.delay < Vue.prototype.$config.map.deviceLagDelay) { return 'green'; }
      if (this.delay < Vue.prototype.$config.map.deviceTimeoutDelay) { return 'orange'; }

      return 'red';
    },

    heartbeatText() {
      if (!this.delay || this.delay === Infinity) {
        return '';
      }

      return ms(Math.max(1000, this.delay));
    },
  },

  mounted() {
    this.interval = setInterval(() => {
      this.currentTime = Date.now();
    }, 5 * 1000);
  },

  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },

  methods: {
    trackDevice(device) {
      const doTrack = !device.track; // Store it before clearing on all devices
      this.$store.dispatch('devices/clearTrack');

      if (!device.show) {
        Vue.set(device, 'show', true);
      }

      Vue.set(device, 'track', doTrack); // eslint-disable-line no-param-reassign
    },

    showDevice(device) {
      if (device.track) {
        Vue.set(device, 'track', false);
      }

      Vue.set(device, 'show', !device.show);

      this.$store.dispatch('preferences/hideAndSave', {
        id: device.id,
        hide: !device.show,
      });
    },

    saveDevice(device) {
      this.$store.commit('devices/updateDevice', { device, refresh: true });
    },
  },
};
</script>

<style>
  .elac-coloricon {
    position: relative;
    width: 15px !important;
    height: 15px;
    float: left;
    border-radius: 4px;
    margin: 6px;
    position: relative;
    outline: none;
  }

  .v-small-dialog__content:has(.vc-compact) {
    padding: 0;
  }
</style>
